import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  FormControlLabel,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import {
  SettingRootField,
  TgopField,
  UpdateItemSettingsImfOnBasket,
  UpdateItemSettingsRootOnBasket,
  UpdateItemSettingsTgopOnBasket,
} from "../../../Redux/Reducers/redux-slice-basket";
import FormSection from "../../Forms/FormSection/form-section";
import { DbOrderBasketItemInfos } from "../../../Core/Api/Types/db-order-basket-item-infos";
import ConfigFormIcon from "../ConfigFormIcon";
import { UserHasAcl } from "../../../Utils/helpers";
import { useDispatch } from "react-redux";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import { OrderProductType } from "../../../Core/Api/Types/order-product-type";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import AddIcon from "@mui/icons-material/Add";
import OutlinedCheckbox from "../../Forms/OutlinedCheckbox";
import { BaseMetadataType } from "../../../Core/Api/Types/base-metadata-type";
import ModalClip, { ModalClipElementProps } from "../../Modals/ModalClip/modal-clip";
import { ModalContext } from "../../Modals";
import { TranskodeOptionTimecodeTrim } from "../../../Core/Api/Types/transkode-option-timecode-trim";
import moment from "moment";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { useTranslation } from "react-i18next";
import { produce } from "immer";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SearchIcon from "@mui/icons-material/Search";

interface ConfigCommonSectionFormClipsProps {
  item: DbOrderBasketItemInfos;
  clips: DbAssetObjectInfos[];
}

const ConfigCommonSectionFormClips: React.FC<ConfigCommonSectionFormClipsProps> = (props) => {
  const { t } = useTranslation();
  const { item, clips } = props;
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };
  const dispatch = useDispatch();
  const [selectedClips, setSelectedClips] = useState<TranskodeOptionTimecodeTrim[]>([]);
  const [createdClips, setCreatedClips] = useState<TranskodeOptionTimecodeTrim[]>([]);
  const [searchPattern, setSearchPattern] = useState<string>("");
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [clipsFetched, setClipsFetched] = useState<boolean>(false);
  const [isInitialConfig, setIsInitialConfig] = useState<boolean>(true);

  const productType: OrderProductType = item?.selectedProduct?.type!;

  const timecodeTrim = useMemo(() => {
    if (productType === OrderProductType.TranscodeGeneric || productType === OrderProductType.TranscodeByProfile)
      return item.selectedProduct?.settings?.tgop.timecodeTrim ?? null;
    if (productType === OrderProductType.TranscodeDcp)
      return item.selectedProduct?.settings?.cpls.selectedValue?.timecodeTrim ?? null;

    return item.selectedProduct?.settings?.timecodeTrim ?? null;
  }, [productType, item.selectedProduct]);

  const listTimecodeTrims = useMemo(() => {
    if (productType === OrderProductType.TranscodeGeneric || productType === OrderProductType.TranscodeByProfile)
      return item.selectedProduct?.settings?.tgop.listTimecodeTrims ?? [];
    if (productType === OrderProductType.TranscodeDcp)
      return item.selectedProduct?.settings?.cpls.selectedValue?.listTimecodeTrims ?? [];

    return item.selectedProduct?.settings?.listTimecodeTrims ?? [];
  }, [productType, item.selectedProduct]);

  const clipIsSelected = useCallback(
    (clipMetadata: any) => {
      if (selectedClips.length === 0) return false;
      return selectedClips.some(
        (x) =>
          (x.frameIn === clipMetadata.value?.frameIn && x.frameOut === clipMetadata.value?.frameOut) ||
          (x.frameIn === clipMetadata.frameIn && x.frameOut === clipMetadata.frameOut)
      );
    },
    [selectedClips]
  );

  const openClipModal = React.useCallback(() => {
    if (!openModal) return;
    openModal<ModalClipElementProps>(ModalClip, {
      item: item,
      onClose: () => {},
      onSubmit: (clip) =>
        setCreatedClips(
          produce((draft) => {
            draft.push(clip);
          })
        ),
    });
  }, [openModal, item]);

  // Update tgop or asinput product (depending of item type) object into redux
  const handleUpdateTranscodeParam = useCallback(
    (value: any, isMulti: boolean) => {
      if (
        item.selectedProduct?.type === OrderProductType.TranscodeGeneric ||
        item.selectedProduct?.type === OrderProductType.TranscodeByProfile
      )
        dispatch(
          UpdateItemSettingsTgopOnBasket({
            itemId: item.id,
            field: isMulti ? TgopField.MultipleTimecodeTrims : TgopField.TimecodeTrim,
            value: value,
          })
        );
      else if (item.selectedProduct?.type === OrderProductType.TranscodeDcp)
        dispatch(
          UpdateItemSettingsImfOnBasket({
            itemId: item.id,
            field: isMulti ? TgopField.MultipleTimecodeTrims : TgopField.TimecodeTrim,
            value: value,
          })
        );
      else {
        dispatch(
          UpdateItemSettingsRootOnBasket({
            itemId: item.id,
            field: isMulti ? SettingRootField.MultipleTimecodeTrims : SettingRootField.TimecodeTrim,
            value: value,
          })
        );
      }
    },
    [item]
  );

  useEffect(() => {
    if (!clipsFetched) {
      setClipsFetched(true);
      return;
    }
    let tcTrim = null;
    if (clipsFetched)
      switch (productType) {
        case OrderProductType.TranscodeDcp:
          tcTrim = item.selectedProduct?.settings?.cpls.selectedValue?.timecodeTrim;
          if (tcTrim) setInitialClipState(tcTrim);
          break;
        case OrderProductType.TranscodeSequence:
        case OrderProductType.TranscodeGenericSingleFile:
          tcTrim = item.selectedProduct?.settings?.timecodeTrim;
          if (tcTrim) setInitialClipState(tcTrim);
          break;
        case OrderProductType.TranscodeGeneric:
        case OrderProductType.TranscodeByProfile:
          tcTrim = item.selectedProduct?.settings?.tgop.timecodeTrim;
          if (tcTrim) setInitialClipState(tcTrim);
          break;
        default:
          break;
      }
  }, [clips]);

  const setInitialClipState = (trim: any) => {
    if (
      !clips.some(
        (clip) =>
          (clip.metadata?.find((x) => x.fieldType === BaseMetadataType.DbClipMetadataValueInfos) as any).value
            .frameRate === trim.frameRate &&
          (clip.metadata?.find((x) => x.fieldType === BaseMetadataType.DbClipMetadataValueInfos) as any).value
            .frameIn === trim.frameIn &&
          (clip.metadata?.find((x) => x.fieldType === BaseMetadataType.DbClipMetadataValueInfos) as any).value
            .frameOut === trim.frameOut
      )
    )
      setCreatedClips([trim]);

    setSelectedClips([trim]);
  };

  useEffect(() => {
    if (
      isInitialConfig ||
      (productType === OrderProductType.TranscodeDcp && !item.selectedProduct?.settings?.cpls.selectedValue)
    )
      return;

    if (selectedClips.length === 0) {
      handleUpdateTranscodeParam(null, false);
      handleUpdateTranscodeParam([], true);
    }
    if (selectedClips.length === 1) {
      handleUpdateTranscodeParam(selectedClips[0], false);
      handleUpdateTranscodeParam([], true);
    } else {
      handleUpdateTranscodeParam(selectedClips[0], false);
      handleUpdateTranscodeParam(selectedClips.slice(1), true);
    }
  }, [selectedClips]);

  const statusText = useMemo(() => {
    const contents: string[] = [];

    if ((!timecodeTrim || timecodeTrim?.length === 0) && (!listTimecodeTrims || listTimecodeTrims?.length === 0))
      return t("form:statusText.configurationIsOptional");

    clips.forEach((clip) => {
      if (clipIsSelected(clip.metadata?.find((x) => x.fieldType === BaseMetadataType.DbClipMetadataValueInfos))) {
        contents.push(clip.name);
      }
    });

    createdClips.forEach((clip) => {
      if (clipIsSelected(clip)) contents.push(clip.name ?? "");
    });

    return contents.join(GlobalConst.FORM_SECTION_SUMMARY_DELIMITER);
  }, [timecodeTrim, listTimecodeTrims, clips, selectedClips]);

  const selectDeselectAll = () => {
    if (selectAll) {
      setSelectedClips([]);
      setIsInitialConfig(false);
    } else {
      let trims: TranskodeOptionTimecodeTrim[] = [];
      clips.forEach((clip) => {
        trims.push({
          frameRate: (clip.metadata?.find((x) => x.fieldType === BaseMetadataType.DbClipMetadataValueInfos) as any)
            .value.frameRate,
          frameIn: (clip.metadata?.find((x) => x.fieldType === BaseMetadataType.DbClipMetadataValueInfos) as any).value
            .frameIn,
          frameOut: (clip.metadata?.find((x) => x.fieldType === BaseMetadataType.DbClipMetadataValueInfos) as any).value
            .frameOut,
          timecodeIn: null,
          timecodeOut: null,
          name: clip.name,
        });
      });

      setSelectedClips([...trims, ...createdClips]);
      setIsInitialConfig(false);
    }
    setSelectAll(!selectAll);
  };

  if (
    !UserHasAcl(AclTypes.aclSegmentCutView) &&
    productType !== OrderProductType.TranscodeGeneric &&
    productType !== OrderProductType.TranscodeByProfile
  )
    return <></>;

  return (
    <FormSection
      icon={<ConfigFormIcon icon="video" />}
      title={t("common:clips")}
      status={timecodeTrim || listTimecodeTrims?.length > 0 ? "complete" : "todo"}
      statusText={statusText}
    >
      {productType === OrderProductType.TranscodeDcp && !item.selectedProduct?.settings?.cpls.selectedValue ? (
        <Alert severity="info">{t("alert:info.selectCpl")}</Alert>
      ) : (
        <>
          <Stack direction="row" justifyContent="space-between" width="100%" mb={2}>
            <Typography color="text.tier">
              <FormControlLabel
                sx={{ mt: 0.5, ml: 0 }}
                control={<Switch size="small" checked={selectAll} onChange={selectDeselectAll} />}
                label={t("common:selectDeselectAll")}
              />
            </Typography>
            <OutlinedInput
              placeholder={t("field:placeholder.filterByName")}
              value={searchPattern}
              onChange={(value) => setSearchPattern(value.target.value)}
              endAdornment={
                <InputAdornment position="end">
                  {searchPattern && (
                    <IconButton
                      aria-label="Clear field"
                      onClick={() => setSearchPattern("")}
                      size="small"
                      sx={{ mr: 1 }}
                    >
                      <CloseRoundedIcon />
                    </IconButton>
                  )}
                  <SearchIcon />
                </InputAdornment>
              }
            />
          </Stack>
          <Stack spacing={0.5}>
            {clips
              .filter((x) => searchPattern === "" || x.name.includesAllWords(searchPattern))
              .sort((n1, n2) => {
                if (n1.creationInfos!.date > n2.creationInfos!.date) return 1;
                else return -1;
              })
              .map((clip, index) => (
                <OutlinedCheckbox
                  key={index}
                  contentProps={{
                    direction: "row",
                    justifyContent: "space-between",
                    spacing: 2,
                  }}
                  checked={clipIsSelected(
                    clip.metadata?.find((x) => x.fieldType === BaseMetadataType.DbClipMetadataValueInfos)
                  )}
                  onClick={() => {
                    const clipDetails = clip.metadata?.find(
                      (x) => x.fieldType === BaseMetadataType.DbClipMetadataValueInfos
                    ) as any;
                    if (clipDetails) {
                      if (clipIsSelected(clipDetails)) {
                        setSelectedClips(
                          produce((draft) => {
                            const index = draft.findIndex(
                              (x) =>
                                x.frameIn === clipDetails?.value.frameIn && x.frameOut === clipDetails?.value.frameOut
                            );
                            if (index !== -1) draft.splice(index, 1);
                          })
                        );
                        setIsInitialConfig(false);
                      } else {
                        let trim: TranskodeOptionTimecodeTrim = {
                          frameRate: clipDetails.value.frameRate,
                          frameIn: clipDetails.value.frameIn,
                          frameOut: clipDetails.value.frameOut,
                          timecodeIn: null,
                          timecodeOut: null,
                          name: clip.name,
                        };

                        setSelectedClips(
                          produce((draft) => {
                            draft.push(trim);
                          })
                        );
                        setIsInitialConfig(false);
                      }
                    }
                  }}
                >
                  <Stack>
                    <Typography color="text.primary" fontWeight={500}>
                      {clip.name}
                    </Typography>
                    <Typography
                      color="text.tier"
                      sx={(theme) => ({ ...theme.helpers(theme).clampedText, WebkitLineClamp: 1 })}
                    >
                      {clip.creationInfos?.userName} -{" "}
                      {moment(clip.creationInfos?.date).format(GlobalConst.MOMENT_DATE_FORMAT_FULL)}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Typography color="primary.text">
                      {
                        clip.metadata?.find((x) => x.fieldType === BaseMetadataType.DbClipMetadataValueInfos)?.value
                          .timecodeIn
                      }
                    </Typography>
                    <Typography color="primary.text">
                      {
                        clip.metadata?.find((x) => x.fieldType === BaseMetadataType.DbClipMetadataValueInfos)?.value
                          .timecodeOut
                      }
                    </Typography>
                  </Stack>
                </OutlinedCheckbox>
              ))}

            {createdClips
              .filter((x) => searchPattern === "" || x.name?.includesAllWords(searchPattern))
              .map((clip, index) => (
                <OutlinedCheckbox
                  key={index}
                  contentProps={{
                    direction: "row",
                    justifyContent: "space-between",
                    spacing: 2,
                  }}
                  checked={clipIsSelected(clip)}
                  onClick={() => {
                    if (clipIsSelected(clip)) {
                      setSelectedClips(
                        produce((draft) => {
                          const index = draft.findIndex(
                            (x) => x.frameIn === clip?.frameIn && x.frameOut === clip.frameOut
                          );
                          if (index !== -1) draft.splice(index, 1);
                        })
                      );
                      setIsInitialConfig(false);
                    } else {
                      setSelectedClips(
                        produce((draft) => {
                          draft.push(clip);
                        })
                      );
                      setIsInitialConfig(false);
                    }
                  }}
                >
                  <Stack>
                    <Typography color="text.primary" fontWeight={500}>
                      {clip.name}
                    </Typography>
                  </Stack>
                </OutlinedCheckbox>
              ))}
          </Stack>
          <Button onClick={openClipModal} startIcon={<AddIcon />} sx={{ mt: clips && clips.length > 0 ? 3 : 0 }}>
            {t("button:createClipFromPlayer")}{" "}
            {timecodeTrim?.timecodeIn ? `(${timecodeTrim?.timecodeIn} - ${timecodeTrim?.timecodeOut})` : ""}
          </Button>
        </>
      )}
    </FormSection>
  );
};

export default ConfigCommonSectionFormClips;
